/**
 * Sync Job Monitor Component
 *
 * Component for monitoring and managing data synchronization jobs
 * with real-time status updates and progress tracking.
 */

import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  PlayArrow as StartIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useSyncJobs } from '../../hooks/useSyncJobs';

interface SyncJobMonitorProps {
  dataSources: any[];
}

export const SyncJobMonitor: React.FC<SyncJobMonitorProps> = ({
  dataSources,
}) => {
  const [selectedDataSource, setSelectedDataSource] = useState<string>('all');
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any>(null);

  const { syncJobs, loading, startSync, refreshJobs } = useSyncJobs(
    selectedDataSource === 'all' ? undefined : selectedDataSource
  );

  useEffect(() => {
    // Refresh jobs every 5 seconds for real-time updates
    const interval = setInterval(() => {
      refreshJobs();
    }, 5000);

    return () => clearInterval(interval);
  }, [refreshJobs]);

  const handleStartSync = async (
    dataSourceId: string,
    type: 'full' | 'incremental'
  ) => {
    await startSync(dataSourceId, type);
  };

  const handleViewJobDetails = (job: any) => {
    setSelectedJob(job);
    setJobDetailsOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'primary';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'running':
        return <ScheduleIcon />;
      default:
        return undefined;
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = end.getTime() - start.getTime();

    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);

    return `${minutes}m ${seconds}s`;
  };

  const getDataSourceName = (dataSourceId: string) => {
    const dataSource = dataSources.find(ds => ds.id === dataSourceId);
    return dataSource?.name || dataSourceId;
  };

  return (
    <Box>
      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Data Source</InputLabel>
            <Select
              value={selectedDataSource}
              label='Data Source'
              onChange={e => setSelectedDataSource(e.target.value)}
            >
              <MenuItem value='all'>All Data Sources</MenuItem>
              {dataSources.map(ds => (
                <MenuItem key={ds.id} value={ds.id}>
                  {ds.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={refreshJobs}
            disabled={loading}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Quick Actions
          </Typography>
          <Box display='flex' gap={2} flexWrap='wrap'>
            {dataSources
              .filter(ds => ds.enabled)
              .map(dataSource => (
                <Box key={dataSource.id} display='flex' gap={1}>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<StartIcon />}
                    onClick={() =>
                      handleStartSync(dataSource.id, 'incremental')
                    }
                  >
                    Sync {dataSource.name}
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => handleStartSync(dataSource.id, 'full')}
                  >
                    Full Sync
                  </Button>
                </Box>
              ))}
          </Box>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data Source</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Records</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {syncJobs.map(job => (
              <TableRow key={job.id}>
                <TableCell>
                  <Typography variant='body2'>
                    {getDataSourceName(job.dataSourceId)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={job.type} size='small' variant='outlined' />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(job.status)}
                    label={job.status}
                    color={getStatusColor(job.status) as any}
                    size='small'
                  />
                </TableCell>
                <TableCell>
                  {job.progress && (
                    <Box sx={{ minWidth: 100 }}>
                      <LinearProgress
                        variant='determinate'
                        value={
                          (job.progress.current / job.progress.total) * 100
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant='caption'>
                        {job.progress.current}/{job.progress.total} -{' '}
                        {job.progress.phase}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>
                    {job.recordsProcessed}
                    {job.recordsSkipped > 0 && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                      >
                        {job.recordsSkipped} skipped
                      </Typography>
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>
                    {formatDuration(job.startedAt, job.completedAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>
                    {new Date(job.startedAt).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title='View Details'>
                    <IconButton
                      size='small'
                      onClick={() => handleViewJobDetails(job)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {syncJobs.length === 0 && (
        <Box textAlign='center' py={4}>
          <Typography variant='h6' color='text.secondary' mb={2}>
            No sync jobs found
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Start a sync job to see it here
          </Typography>
        </Box>
      )}

      {/* Job Details Dialog */}
      <Dialog
        open={jobDetailsOpen}
        onClose={() => setJobDetailsOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Sync Job Details</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant='subtitle2'>Job ID</Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    {selectedJob.id}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant='subtitle2'>Data Source</Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    {getDataSourceName(selectedJob.dataSourceId)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant='subtitle2'>Type</Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    {selectedJob.type}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant='subtitle2'>Status</Typography>
                  <Chip
                    label={selectedJob.status}
                    color={getStatusColor(selectedJob.status) as any}
                    size='small'
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant='subtitle2'>Progress</Typography>
                  {selectedJob.progress && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant='determinate'
                        value={
                          (selectedJob.progress.current /
                            selectedJob.progress.total) *
                          100
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant='body2'>
                        {selectedJob.progress.current}/
                        {selectedJob.progress.total} -{' '}
                        {selectedJob.progress.phase}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant='subtitle2'>Records Processed</Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    {selectedJob.recordsProcessed}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant='subtitle2'>Records Skipped</Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    {selectedJob.recordsSkipped}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant='subtitle2'>Started At</Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    {new Date(selectedJob.startedAt).toLocaleString()}
                  </Typography>
                </Grid>
                {selectedJob.completedAt && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant='subtitle2'>Completed At</Typography>
                    <Typography variant='body2' sx={{ mb: 2 }}>
                      {new Date(selectedJob.completedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {selectedJob.errors && selectedJob.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant='subtitle2' gutterBottom>
                    Errors ({selectedJob.errors.length})
                  </Typography>
                  {selectedJob.errors.map((error: any, index: number) => (
                    <Card
                      key={index}
                      sx={{
                        mb: 1,
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                      }}
                    >
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant='body2'>{error.message}</Typography>
                        <Typography variant='caption'>
                          {new Date(error.timestamp).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
